import { getMarketingProParams } from '@servicetitan/marketingpro-param-collector';
import {
  SOURCE_TRACKING_QUERY_PARAMS,
  type SourceTrackingParams,
} from '@servicetitan/web-chat-types';

// SETO-7267: mirrors scheduler-embed's getEmbedQueryParams.ts backfill loop — a UTM/click-ID key
// missing from the tenant page's own URL gets filled in from marketingpro-param-collector's
// sessionStorage read (a separate DNI script on the tenant page writes that key; the collector
// itself is a synchronous read, not an async fetch).
// affiliate is the already-resolved data-affiliate script-tag attribute (InitOptions) — passed in
// rather than re-read from the DOM here, since init() only has the resolved options, not the
// script element itself. campaign is the same, but also has a ?campaign= URL equivalent (below) —
// unlike affiliate, which stays script-tag-only.
// Called from both init() (initial iframe src) and the webchat:ready handler (late-arriving
// values, since the DNI script's sessionStorage write can race the embed script) — see init.ts.
export function collectSourceTrackingParams(
  affiliate?: string,
  campaign?: string,
): SourceTrackingParams {
  const url = new URL(window.location.href);
  const marketingProParams = safeGetMarketingProParams();

  const params: SourceTrackingParams = {
    referrer: document.referrer,
    landingPageUrl: window.location.href,
    affiliate,
  };

  for (const key of SOURCE_TRACKING_QUERY_PARAMS) {
    params[key] = url.searchParams.get(key) ?? marketingProParams[key] ?? undefined;
  }

  // data-campaign script-tag fallback, applied AFTER the loop above (not folded into the object
  // literal before it, the way affiliate is): campaign is in SOURCE_TRACKING_QUERY_PARAMS, so the
  // loop already gave ?campaign= (or, in practice never, a marketingpro-backfilled value -- that
  // vendor's own key set has no concept of campaign) top priority. This only fills in the
  // script-tag attribute when neither of those produced anything -- if this ran before the loop
  // instead, the loop would unconditionally overwrite it with undefined on a page with no
  // ?campaign= of its own, silently breaking every tenant relying on data-campaign alone.
  params.campaign = params.campaign ?? campaign;

  return dropEmpty(params);
}

// getMarketingProParams() reads window.sessionStorage directly with no guard of its own (only
// its internal JSON.parse is try/caught) -- a realistic storage failure (Safari's "Block All
// Cookies", a storage-partitioned/sandboxed embed context) would otherwise propagate uncaught out
// of this module. That's a bigger blast radius than an analytics nice-to-have warrants: at the
// init() call site it takes down the entire widget (no fallback UI shown), and at the
// webchat:ready re-collection call site there's no outer try/catch at all, so it would silently
// swallow the 'ready' event with no 'error' event to signal it. Falling back to {} here degrades
// to "no tracking params" instead.
function safeGetMarketingProParams(): Record<string, string> {
  try {
    return getMarketingProParams() ?? {};
  } catch {
    return {};
  }
}

function dropEmpty(params: SourceTrackingParams): SourceTrackingParams {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => !!value),
  ) as SourceTrackingParams;
}

// SETO-7267: referrer/landingPageUrl are present on essentially every page load, so their
// presence alone doesn't mean there's anything new worth a postMessage round-trip on
// webchat:ready — mirrors scheduler-embed's handleAppReady.ts shouldSendSourceAnalytics, which
// gates its own re-send on click IDs/UTM/custom fields and deliberately excludes clientUrl/referrer
// (those go out unconditionally via the initial iframe src's query string instead, see iframe.ts).
export function hasAttributionSignal(params: SourceTrackingParams): boolean {
  return Object.entries(params).some(
    ([key, value]) => key !== 'referrer' && key !== 'landingPageUrl' && !!value,
  );
}
